import { cn } from '@neo/lib/utils'
import type { ReactNode } from 'react'

interface MethodTileProps<T extends string> {
  method: T
  // undefined = no mode chosen yet, so no tile reads as selected.
  selected: T | undefined
  onSelect: (method: T) => void
  icon: ReactNode
  title: string
  subtitle: string
  badge?: string
  disabled?: boolean
  // Drops the tile's own outline so it can sit inside a wrapper that owns it —
  // used when the method carries a payload below it (the card list) and the two
  // have to read as one block.
  bordered?: boolean
}

export const MethodTile = <T extends string>({
  method,
  selected,
  onSelect,
  icon,
  title,
  subtitle,
  badge,
  disabled,
  bordered = true,
}: MethodTileProps<T>) => (
  <button
    type="button"
    aria-pressed={selected === method}
    onClick={() => !disabled && onSelect(method)}
    className={cn(
      'flex items-center justify-between w-full px-3 py-3 text-left transition-colors',
      bordered &&
        (selected === method
          ? 'rounded border border-primary'
          : 'rounded border border-border hover:border-hv1'),
      disabled && 'opacity-50 cursor-not-allowed',
    )}
    data-testid={`add-balance-method-${method}`}
    disabled={disabled}
  >
    <div className="flex items-center gap-3">
      {/* Fixed 36px column, the same width as the card row's brand logo, so the
          title here and the card number there start on one left edge instead of
          each being offset by its own icon. The icon centres in it, so whatever
          slack the column has falls evenly on both sides. */}
      <span className="flex h-8 w-9 shrink-0 items-center justify-center">{icon}</span>
      <div>
        <div className="text-p-600">{title}</div>
        <div className="text-s-500 text-secondary">{subtitle}</div>
      </div>
    </div>
    {badge && (
      <span className="text-s-500 px-2 py-0.5 rounded bg-success-tag text-success-tag-foreground">
        {badge}
      </span>
    )}
  </button>
)
